/** @prettier */

import {Grid, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';

//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack, iconWhite} from '../../define';

/**
 * Action欄のアイコン表示
 * @param props props
 * - scheduleInfo スケジュール設定情報
 * @returns JSX
 */
export const ScheduleActionControlContents = (props: {
  scheduleInfo: apiIF.reqParameters.schedule;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 運転/停止アイコン
   * @param {apiIF.res.command['run-stop']} runStopState  対象スケジュールの運転/停止状態
   * @returns アイコン画像
   */
  const runStopIcon = (runStopState: apiIF.res.command['run-stop']) => {
    switch (runStopState) {
      case 'run':
        return {
          src: iconWhite.AC_ON.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.AC_ON.alt}`
          ),
        };
      case 'stop':
        return {
          src: iconWhite.AC_OFF.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.AC_OFF.alt}`
          ),
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconBlack.MISSING.alt}`
          ),
        };
    }
  };

  /**
   * 運転モードアイコン
   * @param {apiIF.res.command['operation-mode']} modeState  対象スケジュールの運転モード
   * @returns アイコン画像
   */
  const modeIcon = (modeState: apiIF.res.command['operation-mode']) => {
    switch (modeState) {
      case 'auto':
        return {
          src: iconWhite.AUTO.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.AUTO.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--auto',
        };
      case 'dehumidify':
        return {
          src: iconWhite.DRYING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.DRYING.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--dry',
        };
      case 'cooling':
        return {
          src: iconWhite.COOLING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.COOLING.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--cooling',
        };
      case 'fan':
        return {
          src: iconWhite.BLOWER.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.BLOWER.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--fan',
        };
      case 'heating':
        return {
          src: iconWhite.HEATING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.HEATING.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--heating',
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconBlack.MISSING.alt}`
          ),
          class:
            'schedule__table__body__row__item__action__operationModeIcon--non',
        };
    }
  };

  /**
   * 風量アイコン
   * @param {apiIF.res.command['air-flow']} modeState  対象スケジュールの風量
   * @returns アイコン画像
   */
  const fanSpeedIcon = (modeState: apiIF.res.command['air-flow']) => {
    switch (modeState) {
      case 'auto':
        return {
          src: iconWhite.FAN_SPEED_AUTO.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.FAN_SPEED_AUTO.alt}`
          ),
        };
      case 'low':
        return {
          src: iconWhite.FAN_SPEED_1.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.FAN_SPEED_1.alt}`
          ),
        };
      case 'middle':
        return {
          src: iconWhite.FAN_SPEED_2.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.FAN_SPEED_2.alt}`
          ),
        };
      case 'high':
        return {
          src: iconWhite.FAN_SPEED_3.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.FAN_SPEED_3.alt}`
          ),
        };
      case 'powerful':
        return {
          src: iconWhite.FAN_SPEED_4.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconWhite.FAN_SPEED_4.alt}`
          ),
        };
      default:
        return {
          src: iconBlack.MISSING.src,
          alt: trans(
            `ScheduleActionControlIconContents.image.alternativeText.${iconBlack.MISSING.alt}`
          ),
        };
    }
  };

  return (
    <Grid container alignItems="center" columnSpacing={2}>
      {/** 運転/停止 */}
      <Grid item xs={3}>
        {'run-stop' in props.scheduleInfo &&
        props.scheduleInfo['run-stop'] !== undefined ? (
          <Box className="schedule__table__body__row__item__action__runStopIcon">
            <img
              src={runStopIcon(props.scheduleInfo['run-stop']).src}
              title={runStopIcon(props.scheduleInfo['run-stop']).alt}
              alt={runStopIcon(props.scheduleInfo['run-stop']).alt}
              className="schedule__table__body__row__item__action__runStopIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 運転モード */}
      <Grid item xs={3}>
        {'operation-mode' in props.scheduleInfo &&
        props.scheduleInfo['operation-mode'] !== undefined ? (
          <Box className={modeIcon(props.scheduleInfo['operation-mode']).class}>
            <img
              src={modeIcon(props.scheduleInfo['operation-mode']).src}
              alt={modeIcon(props.scheduleInfo['operation-mode']).alt}
              title={modeIcon(props.scheduleInfo['operation-mode']).alt}
              className="schedule__table__body__row__item__action__operationModeIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 風量 */}
      <Grid item xs={3}>
        {'air-flow' in props.scheduleInfo &&
        props.scheduleInfo['air-flow'] !== undefined ? (
          <Box className="schedule__table__body__row__item__action__fanSpeedIcon">
            <img
              src={fanSpeedIcon(props.scheduleInfo['air-flow']).src}
              alt={fanSpeedIcon(props.scheduleInfo['air-flow']).alt}
              title={fanSpeedIcon(props.scheduleInfo['air-flow']).alt}
              className="schedule__table__body__row__item__action__fanSpeedIcon__image"
            />
          </Box>
        ) : (
          <></>
        )}
      </Grid>
      {/** 設定温度 */}
      <Grid item xs={3}>
        {'preset-temperature' in props.scheduleInfo &&
        props.scheduleInfo['preset-temperature'] !== '' ? (
          <>{props.scheduleInfo['preset-temperature']}&nbsp;°C</>
        ) : (
          <></>
        )}
      </Grid>
    </Grid>
  );
};
export default ScheduleActionControlContents;
